import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  Modal,
  Box,
  Button,
  Text,
  Flex,
  Icon,
  H3,
  H6,
  FlexItem,
  Divider,
} from "orcs-design-system";
import { keyBy } from "lodash";
import { API_DATE_FORMAT } from "src/consts/global";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import useRestricted from "src/app/Restricted/useRestricted";
import { useOrgChartContext } from "../../context/OrgChartContext";
import { ACTION_TYPES } from "../../context/actionTypes";
import { generateChangelogSummary } from "../../utils/changelog/generateChangelogSummary";
import { processChangelogData } from "../../utils/changelog/processChangelogData";
import { exportChangelogToCSV } from "../../utils/changelog/exportToCSV";
import useGetTeamsForOrgChart, {
  ORG_CHART_STATE_KEYS,
} from "../../hooks/useGetTeamsForOrgChart";
import ChangelogSummary from "./ChangelogSummary";
import ChangelogDetails from "./ChangelogDetails";

const ChangelogView = () => {
  const { state, dispatch } = useOrgChartContext();
  const workspace = useWorkspace();
  const [isDirectoryUser, isPowerUser, isAdmin] = useRestricted();
  const { showChangelog, fromDate, toDate } = state?.viewOptions || {};

  // Only allow power users and admins to access changelog
  const hasAccess = !isDirectoryUser && (isPowerUser || isAdmin);
  const { teams = {}, members = {} } = state || {};
  const includeHiddenDeleted = false;
  const [baselineData, setBaselineData] = useState({ teams: {}, members: {} });
  const [nextData, setNextData] = useState({ teams: {}, members: {} });

  // Extract stable team and member IDs to avoid unstable object references in useEffect
  const teamIds = useMemo(
    () =>
      Object.keys(teams).filter(
        (id) => teams[id]?.diff && Object.keys(teams[id].diff).length > 0
      ),
    [teams]
  );
  const memberIds = useMemo(
    () =>
      Object.keys(members).filter(
        (id) => members[id]?.diff && Object.keys(members[id].diff).length > 0
      ),
    [members]
  );

  // Store data loader methods in refs to avoid dependency on loader objects
  const baselineLoaderRef = useRef();
  const nextLoaderRef = useRef();

  // Create mock state with showChanges: true so diff data queries execute
  const mockStateForLoader = {
    ...state,
    viewOptions: {
      ...state?.viewOptions,
      showChanges: true, // Required for diff data queries to execute
    },
  };

  const baselineDataLoader = useGetTeamsForOrgChart({
    pitDate: fromDate?.format(API_DATE_FORMAT) || null,
    teamId: state?.currentViewTeam?.id,
    setState: () => {},
    stateKey: ORG_CHART_STATE_KEYS.DIFF_BASELINE,
    state: mockStateForLoader,
  });

  const nextDataLoader = useGetTeamsForOrgChart({
    pitDate: toDate?.format(API_DATE_FORMAT) || null,
    teamId: state?.currentViewTeam?.id,
    setState: () => {},
    stateKey: ORG_CHART_STATE_KEYS.DIFF_NEXT,
    state: mockStateForLoader,
  });

  // Update refs with current loader instances
  baselineLoaderRef.current = baselineDataLoader;
  nextLoaderRef.current = nextDataLoader;

  // Load baseline and next data asynchronously - don't block modal rendering
  useEffect(() => {
    if (!showChangelog || !fromDate || !toDate) {
      // Reset state when modal closes
      setBaselineData({ teams: {}, members: {} });
      setNextData({ teams: {}, members: {} });
      return undefined;
    }

    const loadBaselineNextData = async () => {
      try {
        const batchSize = 5;
        const baselineTeams = {};
        const nextTeams = {};
        let baselineMembers = {};
        let nextMembers = {};

        for (let i = 0; i < teamIds.length; i += batchSize) {
          const batch = teamIds.slice(i, i + batchSize);
          const baselinePromises = batch.map((id) =>
            baselineLoaderRef.current
              ?.loadSpecificTeamDataById?.(id, {
                fetchMembers: true,
                memberOffset: 0,
                fetchTeams: true,
                teamOffset: 0,
              })
              .catch((err) => {
                console.warn(
                  `Failed to load baseline data for team ${id}:`,
                  err
                );
                return null;
              })
          );
          const nextPromises = batch.map((id) =>
            nextLoaderRef.current
              ?.loadSpecificTeamDataById?.(id, {
                fetchMembers: true,
                memberOffset: 0,
                fetchTeams: true,
                teamOffset: 0,
              })
              .catch((err) => {
                console.warn(`Failed to load next data for team ${id}:`, err);
                return null;
              })
          );

          const [baselineResults, nextResults] = await Promise.all([
            Promise.all(baselinePromises),
            Promise.all(nextPromises),
          ]);

          const batchBaselineTeams = {};
          const batchNextTeams = {};
          const batchBaselineMembers = {};
          const batchNextMembers = {};

          baselineResults.forEach((result) => {
            if (result?.teams) {
              Object.assign(batchBaselineTeams, result.teams);
            }
            if (result?.members) {
              Object.assign(batchBaselineMembers, result.members);
            }
          });
          nextResults.forEach((result) => {
            if (result?.teams) {
              Object.assign(batchNextTeams, result.teams);
            }
            if (result?.members) {
              Object.assign(batchNextMembers, result.members);
            }
          });

          Object.assign(baselineTeams, batchBaselineTeams);
          Object.assign(nextTeams, batchNextTeams);
          baselineMembers = { ...baselineMembers, ...batchBaselineMembers };
          nextMembers = { ...nextMembers, ...batchNextMembers };

          // Update state incrementally so UI can show progress
          setBaselineData((prev) => ({
            teams: { ...prev.teams, ...batchBaselineTeams },
            members: { ...prev.members, ...batchBaselineMembers },
          }));
          setNextData((prev) => ({
            teams: { ...prev.teams, ...batchNextTeams },
            members: { ...prev.members, ...batchNextMembers },
          }));
        }

        if (memberIds.length > 0) {
          try {
            const [baselineMemberResults, nextMemberResults] =
              await Promise.all([
                baselineLoaderRef.current
                  ?.loadMissingMembersForDiff?.(memberIds)
                  .catch((err) => {
                    console.warn("Failed to load baseline members:", err);
                    return [];
                  }),
                nextLoaderRef.current
                  ?.loadMissingMembersForDiff?.(memberIds)
                  .catch((err) => {
                    console.warn("Failed to load next members:", err);
                    return [];
                  }),
              ]);

            if (baselineMemberResults) {
              const additionalBaselineMembers = keyBy(
                baselineMemberResults,
                "aggregateId"
              );
              baselineMembers = {
                ...baselineMembers,
                ...additionalBaselineMembers,
              };
            }
            if (nextMemberResults) {
              const additionalNextMembers = keyBy(
                nextMemberResults,
                "aggregateId"
              );
              nextMembers = { ...nextMembers, ...additionalNextMembers };
            }

            setBaselineData((prev) => ({
              ...prev,
              members: { ...prev.members, ...baselineMembers },
            }));
            setNextData((prev) => ({
              ...prev,
              members: { ...prev.members, ...nextMembers },
            }));
          } catch (error) {
            console.warn("Error loading member data:", error);
          }
        }

        setBaselineData({ teams: baselineTeams, members: baselineMembers });
        setNextData({ teams: nextTeams, members: nextMembers });
      } catch (error) {
        console.error("Error loading baseline/next data:", error);
      }
    };

    const timeoutId = setTimeout(() => {
      loadBaselineNextData();
    }, 100);

    const cleanup = () => {
      clearTimeout(timeoutId);
    };
    return cleanup;
    // Note: baselineLoaderRef and nextLoaderRef are intentionally excluded from dependencies
    // as they are stored in refs to avoid re-triggering the effect when loader objects change
  }, [showChangelog, fromDate, toDate, teamIds, memberIds]);

  const handleClose = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.UPDATE_VIEW_OPTIONS,
      payload: {
        ...state.viewOptions,
        showChangelog: false,
      },
    });
  }, [dispatch, state.viewOptions]);

  const changelogData = useMemo(() => {
    if (!showChangelog || !fromDate || !toDate) {
      return null;
    }

    let filteredTeams = teams;
    let filteredMembers = members;

    if (!includeHiddenDeleted) {
      filteredTeams = Object.fromEntries(
        Object.entries(teams).filter(
          ([, team]) => !team.isHidden && !team.isDeleted
        )
      );

      filteredMembers = Object.fromEntries(
        Object.entries(members).filter(([, member]) => {
          if (!member.diff) {
            return true;
          }
          // Check if member's teams in diff are all hidden/deleted
          return Object.keys(member.diff).some((teamId) => {
            const team = teams[teamId];
            return team && !team.isHidden && !team.isDeleted;
          });
        })
      );
    }

    // Process with all teams/members first, then filter entries
    const entries = processChangelogData(
      baselineData,
      nextData,
      teams,
      members,
      {
        from: fromDate.format(API_DATE_FORMAT),
        to: toDate.format(API_DATE_FORMAT),
      }
    );

    const isTeamHiddenOrDeletedById = (teamId) => {
      if (!teamId) {
        return false;
      }
      const team = teams[teamId];
      return team && (team.isHidden || team.isDeleted);
    };

    const isEntryHiddenOrDeleted = (entry) => {
      if (isTeamHiddenOrDeletedById(entry.entityId)) {
        return false;
      }
      if (entry.entity && (entry.entity.isHidden || entry.entity.isDeleted)) {
        return false;
      }
      if (entry.from && isTeamHiddenOrDeletedById(entry.from)) {
        return false;
      }
      if (entry.to && isTeamHiddenOrDeletedById(entry.to)) {
        return false;
      }
      if (
        entry.fromTeam &&
        (entry.fromTeam.isHidden || entry.fromTeam.isDeleted)
      ) {
        return false;
      }
      if (entry.toTeam && (entry.toTeam.isHidden || entry.toTeam.isDeleted)) {
        return false;
      }
      return true;
    };

    const isMemberEntryHiddenOrDeleted = (entry) => {
      if (entry.from && isTeamHiddenOrDeletedById(entry.from)) {
        return false;
      }
      if (entry.to && isTeamHiddenOrDeletedById(entry.to)) {
        return false;
      }
      if (
        entry.fromTeam &&
        (entry.fromTeam.isHidden || entry.fromTeam.isDeleted)
      ) {
        return false;
      }
      if (entry.toTeam && (entry.toTeam.isHidden || entry.toTeam.isDeleted)) {
        return false;
      }
      return true;
    };

    let filteredEntries = entries;
    if (!includeHiddenDeleted) {
      filteredEntries = {
        teams: (entries.teams || []).filter(isEntryHiddenOrDeleted),
        members: (entries.members || []).filter(isMemberEntryHiddenOrDeleted),
        allocations: entries.allocations || [],
        attributes: entries.attributes || [],
        tags: (entries.tags || []).filter((entry) => {
          if (entry.entityType === "team") {
            return !isTeamHiddenOrDeletedById(entry.entityId);
          }
          return true;
        }),
      };
    }

    const summary = generateChangelogSummary(filteredTeams, filteredMembers);
    const memberEntriesWithTagsAndAllocations = new Set();

    (filteredEntries.members || []).forEach((entry) => {
      memberEntriesWithTagsAndAllocations.add(entry.entityId);
    });

    (filteredEntries.tags || [])
      .filter((tagEntry) => tagEntry.entityType === "member")
      .forEach((tagEntry) => {
        const hasMembershipEntry = (filteredEntries.members || []).some(
          (m) => m.entityId === tagEntry.entityId
        );
        if (!hasMembershipEntry) {
          memberEntriesWithTagsAndAllocations.add(tagEntry.entityId);
          summary.members.changed = (summary.members.changed || 0) + 1;
        }
      });

    (filteredEntries.allocations || []).forEach((allocEntry) => {
      const hasMembershipEntry = (filteredEntries.members || []).some(
        (m) => m.entityId === allocEntry.entityId
      );
      if (!hasMembershipEntry) {
        memberEntriesWithTagsAndAllocations.add(allocEntry.entityId);
        summary.members.changed = (summary.members.changed || 0) + 1;
      }
    });

    summary.members.total = memberEntriesWithTagsAndAllocations.size;

    return {
      summary,
      entries: filteredEntries,
      filteredTeams,
      filteredMembers,
      dateRange: {
        from: fromDate.format(API_DATE_FORMAT),
        to: toDate.format(API_DATE_FORMAT),
        fromMoment: fromDate,
        toMoment: toDate,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChangelog, fromDate, toDate, teams, members, baselineData, nextData]);

  const handleExport = useCallback(() => {
    if (changelogData) {
      exportChangelogToCSV(
        changelogData,
        changelogData.dateRange,
        workspace?.config
      );
    }
  }, [changelogData, workspace?.config]);

  if (!hasAccess || !showChangelog || !changelogData) {
    return null;
  }

  const modalFooter = (
    <Flex gap="m" justifyContent="flex-end">
      <Button onClick={handleExport} variant="ghost" size="small">
        <Flex alignItems="center" gap="xs" as="span">
          <Icon icon={["fas", "download"]} size="s" />
          Export to CSV
        </Flex>
      </Button>
      <Button onClick={handleClose} variant="secondary">
        Close
      </Button>
    </Flex>
  );

  return (
    <Modal
      visible={showChangelog}
      onClose={handleClose}
      title="Changelog"
      footer={modalFooter}
      width="90%"
      minWidth="900px"
    >
      <Box p="xl">
        <Box mb="m">
          <H3 mb="m" fontSize="l" fontWeight="bold">
            Summary
          </H3>
          <Divider mb="m" />
          <Box mb="m" py="s">
            <Flex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <FlexItem flex="0 0 auto">
                <Flex alignItems="center" gap="m" flexWrap="wrap">
                  <Flex alignItems="center" gap="m">
                    <H6
                      fontSize="s"
                      fontWeight="semibold"
                      color="textSecondary"
                      uppercase
                      m={0}
                    >
                      Date Range
                    </H6>
                    <Text
                      fontSize="s"
                      fontWeight="semibold"
                      color="text"
                      ml="l"
                    >
                      From: {changelogData.dateRange.from}
                    </Text>
                    <Text
                      fontSize="s"
                      fontWeight="semibold"
                      color="text"
                      ml="xl"
                    >
                      To: {changelogData.dateRange.to}
                    </Text>
                  </Flex>
                </Flex>
              </FlexItem>
              <FlexItem flex="0 0 auto" ml="auto" minWidth="140px">
                <Button onClick={handleExport} variant="ghost" size="small">
                  <Flex alignItems="center" gap="xs" as="span">
                    <Icon icon={["fas", "download"]} size="s" />
                    Export to CSV
                  </Flex>
                </Button>
              </FlexItem>
            </Flex>
          </Box>
          <ChangelogSummary summary={changelogData.summary} />
        </Box>
        <ChangelogDetails
          entries={changelogData.entries}
          teams={teams}
          filteredTeams={changelogData.filteredTeams}
          members={changelogData.filteredMembers}
        />
      </Box>
    </Modal>
  );
};

export default ChangelogView;
